import isUndefined from 'lodash/isUndefined'

import { ITableColumn } from '@makyoapp/makyo-sdk/models/Table'

import { Cell, ICell } from '.'
import { INumberColumnSettings, NumberCell } from './number'

export interface IMathColumnSettings extends INumberColumnSettings {
  /**
   * The expression to evaluate
   * Ex: `_word1_ + _word2_ *5`. The columns are referenced by their id in the columns array.
   * To find the index we use the `_word1_` and `_word2_` in the expression knowing that `_word1_` is the first column and `_word2_` is the second column, etc.
   */
  expr: string
  /**
   * The variables to use in the expression
   * its gonna be a map of words to column ids
   */
  vars: string[]
  columns: string[] // The columns to use in order of appearance in the expression
}

export interface IMathColumn extends ITableColumn {
  Settings: IMathColumnSettings
}

export interface IMathCell extends ICell {
  settings: IMathColumnSettings
}

export class MathCell extends NumberCell implements IMathCell {
  value: Cell
  settings: IMathColumnSettings

  constructor(value: Cell, settings: IMathColumnSettings) {
    super(value, settings)

    this.value = value
    this.settings = {
      expr: settings.expr,
      vars: settings.vars,
      columns: settings.columns,

      // number settings
      numAbbreviate: settings?.numAbbreviate,
      numDecimalSeparator: settings.numDecimalSeparator,
      numPrecision: settings.numPrecision,
      numPrefix: settings.numPrefix,
      numSuffix: settings.numSuffix,
      numThousandSeparator: settings.numThousandSeparator,
    }
  }

  toString(): string {
    if (typeof this.value !== 'number' || this.value === null) {
      return ''
    }

    let num = this.value

    // Handle precision
    let precision = isUndefined(this.settings.numPrecision)
      ? undefined
      : Math.abs(Math.log10(parseFloat(this.settings.numPrecision))) || 0

    let numStr = isUndefined(precision) ? num.toString() : num.toFixed(precision)

    // Handle decimal separator
    if (this.settings.numDecimalSeparator !== 'dot') {
      const decimalSeparator = this.settings.numDecimalSeparator === 'comma' ? ',' : '.'
      numStr = numStr.replace('.', decimalSeparator)
    }

    // Handle thousand separator
    if (this.settings.numThousandSeparator !== 'none') {
      const thousandSeparator =
        this.settings.numThousandSeparator === 'comma' ? ',' : this.settings.numThousandSeparator === 'dot' ? '.' : ' '
      const parts = numStr.split(this.settings.numDecimalSeparator === 'comma' ? ',' : '.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator)
      numStr = parts.join(this.settings.numDecimalSeparator === 'comma' ? ',' : '.')
    }

    // Handle abbreviation
    if (this.settings?.numAbbreviate) {
      const abbreviations = ['', 'K', 'M', 'B', 'T']
      let index = 0
      while (num >= 1000 && index < abbreviations.length - 1) {
        num /= 1000
        index++
      }
      numStr = num.toFixed(precision) + abbreviations[index]
    }

    let prefix = this.settings.numPrefix ?? ''
    let suffix = this.settings.numSuffix ?? ''

    // Add prefix and suffix
    numStr = `${prefix}${numStr}${suffix}`

    return numStr
  }
}
